import { ComponentType, ReactNode, useEffect, useMemo } from 'react';

import { useTheme } from '@mui/material';
import dayjs from 'dayjs';

import {
  Box,
  ChartContainer,
  ChartGrid,
  ChartSelectionProvider,
  ChartTooltip,
  ChartTooltipComponentProps,
  ChartTooltipTimestampHeader,
  DataPopper,
  DataPopperRow,
  LayoutCard,
  Line,
  ReferenceArea,
  Stack,
  TimeXAxis,
  Typography,
  YAxis,
  defaultTimeTicksConfig,
  getTickFormatter,
  tokens,
  useChartSelectionState,
} from '@cast/design-system';
import type { BillingV1FeatureUsageDetail } from '@cast/types';

import {
  buildAnalyzePrompt,
  useAnalyzeWithOpsPilot,
} from '@cast/features-organization-shared-opspilot';

import {
  REFERENCE_BAND_MARGIN_BOTTOM,
  ReferenceBand,
  ReferenceBandStrip,
} from './ReferenceBandStrip';
import {
  SummaryMetric,
  SummaryMetricsRow,
  SummaryMetricsSkeleton,
} from './SummaryMetrics';

// ─── Palette ────────────────────────────────────────────────────────────────

/**
 * Canonical color palette for series, expressed as MUI theme palette token
 * strings (e.g. 'indigo.700'). Resolve them with useTheme() before use.
 * Matches Figma Monetization chart swatches.
 */
const CHART_COLORS: string[] = [
  'indigo.700',
  'blue.700',
  'green.700',
  'yellow.700',
  'red.700',
  'blue.300',
  'indigo.300',
  'green.500',
  'indigo.200',
  'grey.500',
  'orange.700',
  'purple.700',
  'teal.700',
];

const TOP_SERIES_COUNT = 5;
const OTHERS_SERIES_ID = '__others__';

/**
 * Stable entity key. Entities from the billing platform-usage-detail API can
 * arrive with an entityName but no entityId — fall back to the name so the
 * same entity matches across chart filtering/highlighting/aggregation and the
 * entities table rowKey.
 */
export const getEntityKey = (e: { entityId?: string; entityName?: string }) =>
  e.entityId ?? e.entityName ?? '';

// ─── Types ───────────────────────────────────────────────────────────────────

/** Sentinel key used for the Total series – never an entity ID. */
const TOTAL_KEY = '__total__';

type Series = {
  id: string;
  label: string;
  color: string;
  total: number;
};

export type UsageMode = 'organizations' | 'total';

type ChartDataPoint = {
  timestamp: string;
  chartTimestampUnix: number;
  values: Record<string, number | null>;
};

type ReferenceBandCheck = {
  startUnix: number;
  endUnix: number;
  label?: string;
};

// ─── Local timezone ──────────────────────────────────────────────────────────

const LOCAL_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

// ─── Tick config ─────────────────────────────────────────────────────────────

const dailyTicksConfig = {
  ...defaultTimeTicksConfig,
  '3months': {
    ...defaultTimeTicksConfig['3months'],
    timeTickFormatter: getTickFormatter('D'),
  },
};

const yTickFormatter = (value: number | string) => `${value}`;

// ─── Custom tooltip ──────────────────────────────────────────────────────────

type TooltipSeriesInfo = { id: string; label: string; color: string };

type MakeTooltipComponentOptions = {
  seriesInfo: TooltipSeriesInfo[];
  referenceBandChecks: ReferenceBandCheck[];
  unit: string;
  totalColor: string;
  mode: UsageMode;
};

const makeTooltipComponent = ({
  seriesInfo,
  referenceBandChecks,
  unit,
  totalColor,
  mode,
}: MakeTooltipComponentOptions): ComponentType<
  ChartTooltipComponentProps<ChartDataPoint>
> => {
  const colorToSeries = new Map(seriesInfo.map((s) => [s.color, s]));
  // Map total line by its color so the tooltip can label it
  if (mode === 'total') {
    colorToSeries.set(totalColor, {
      id: TOTAL_KEY,
      label: 'Total',
      color: totalColor,
    });
  }

  return function TooltipComponent({
    payload,
  }: ChartTooltipComponentProps<ChartDataPoint>) {
    if (!payload?.length) return null;

    const dataPoint = payload[0].payload;
    const ts = dataPoint.chartTimestampUnix;

    const activeBand = referenceBandChecks.find(
      (b) => ts >= b.startUnix && ts <= b.endUnix
    );

    // Exclude the __total__ entry from the per-entity sum to avoid double-counting
    const total = Object.entries(dataPoint.values)
      .filter(([k]) => k !== TOTAL_KEY)
      .reduce((sum, [, v]) => (sum ?? 0) + (v ?? 0), 0);

    return (
      <DataPopper size="small">
        <Stack
          direction="row"
          alignItems="center"
          gap={8}
          sx={{ px: 8, pt: 4, pb: 2 }}
        >
          <ChartTooltipTimestampHeader
            data={dataPoint}
            testId="usage-chart-tooltip-header"
            timezone={LOCAL_TIMEZONE}
          />
          {activeBand?.label && (
            <Box
              sx={{
                bgcolor: tokens.theme.bg.status.successStrong,
                color: tokens.theme.fg.status.onSuccessStrong,
                fontWeight: 600,
                fontSize: 9,
                px: 6,
                py: 2,
                borderRadius: '4px',
                mb: 4,
                whiteSpace: 'nowrap',
                letterSpacing: '0.5px',
              }}
            >
              {activeBand.label}
            </Box>
          )}
        </Stack>
        <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
          {[...payload]
            .sort((a, b) => (Number(b.value) || 0) - (Number(a.value) || 0))
            .map((p, i) => {
              const color = p.stroke ?? '';
              const series = colorToSeries.get(color);
              const value = Number(p.value) || 0;
              const pct =
                (total ?? 0) > 0
                  ? ((value / (total ?? 0)) * 100).toFixed(1)
                  : '0.0';
              const isBanded = !!activeBand;

              return (
                <DataPopperRow
                  key={i}
                  testId={`usage-chart-tooltip-row-${i}`}
                  indicator={{ color }}
                  left={
                    <Typography
                      color={tokens.theme.fg.structural.primary}
                      variant="P10R"
                    >
                      {series?.label ?? ''}
                    </Typography>
                  }
                  right={
                    <Stack direction="row" gap={4} alignItems="baseline">
                      <Typography
                        variant="P10R"
                        color={
                          isBanded
                            ? tokens.theme.fg.status.success
                            : tokens.theme.fg.structural.primary
                        }
                      >
                        {value.toLocaleString('en-US', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </Typography>
                      <Typography
                        variant="P10R"
                        color={
                          isBanded
                            ? tokens.theme.fg.status.success
                            : tokens.theme.fg.structural.secondary
                        }
                      >
                        {unit}
                      </Typography>
                      <Typography
                        variant="P10R"
                        color={tokens.theme.fg.structural.secondary}
                      >
                        {pct}%
                      </Typography>
                    </Stack>
                  }
                />
              );
            })}
        </Box>
      </DataPopper>
    );
  };
};

// ─── Component ───────────────────────────────────────────────────────────────

type UsageOverTimeChartProps = {
  detail: BillingV1FeatureUsageDetail | undefined;
  isLoading: boolean;
  /** Empty = show top 5 by usage (+ Others); non-empty = only selected entities. */
  selectedEntityIds: string[];
  actions?: ReactNode;
  /**
   * Optional controls rendered in the header, to the left of `actions`.
   * Enterprise view uses this to inject an Organizations / Total toggle.
   */
  headerControls?: ReactNode;
  periodFrom?: string; // YYYY-MM-DD
  periodTo?: string; // YYYY-MM-DD
  /** Called whenever the resolved color map changes, so the table can sync row colors. */
  onEntityColorMapChange?: (colorMap: Map<string, string>) => void;
  /** LayoutCard title — Figma reporting uses "Provisioned vCPUs". */
  title?: string;
  /** Aggregate summary label — Figma reporting uses "Total billable CPUs". */
  totalLabel?: string;
  /**
   * Controls which series are rendered.
   * - 'organizations' (default): per-entity lines, no Total line.
   * - 'total': Total dotted line only, no per-entity lines.
   */
  mode?: UsageMode;
};

export const UsageOverTimeChart = ({
  detail,
  isLoading,
  selectedEntityIds,
  actions,
  headerControls,
  periodFrom,
  periodTo,
  onEntityColorMapChange,
  title = 'Usage over time',
  totalLabel = 'Total usage',
  mode = 'organizations',
}: UsageOverTimeChartProps) => {
  const theme = useTheme();

  const analyzeWithOpsPilot = useAnalyzeWithOpsPilot({
    buildPrompt: buildAnalyzePrompt({
      context: 'platform usage over time',
      getDefaultRange: undefined,
    }),
  });

  const resolveColor = useMemo(
    () =>
      (token: string): string => {
        const parts = token.split('.');
        let obj: Record<string, unknown> = theme.palette as never;
        for (const part of parts)
          obj = (obj?.[part] ?? {}) as Record<string, unknown>;
        return typeof obj === 'string' ? obj : token;
      },
    [theme.palette]
  );

  // Fixed neutral color for the Total line – not from the entity palette.
  const totalLineColor = useMemo(
    () => resolveColor('grey.800'),
    [resolveColor]
  );

  const { series, chartData, entityColorMap, hasTotalData } = useMemo(() => {
    const rangeStart = periodFrom ? dayjs(periodFrom) : null;
    const rangeEnd = periodTo ? dayjs(periodTo) : null;
    const entities = detail?.entities ?? [];

    const sorted = [...entities].sort(
      (a, b) => (b.usage ?? 0) - (a.usage ?? 0)
    );

    const colorMap = new Map<string, string>();
    const highlightIds =
      selectedEntityIds.length > 0
        ? selectedEntityIds
        : sorted.slice(0, TOP_SERIES_COUNT).map(getEntityKey);

    highlightIds.forEach((id, index) => {
      if (!id) return;
      colorMap.set(id, resolveColor(CHART_COLORS[index % CHART_COLORS.length]));
    });

    const topSeries: Series[] = sorted
      .filter((e) => colorMap.has(getEntityKey(e)))
      .map((e) => {
        const id = getEntityKey(e);
        return {
          id,
          label: e.entityName ?? id,
          color: colorMap.get(id)!,
          total: e.usage ?? 0,
        };
      });

    const rest =
      selectedEntityIds.length === 0
        ? sorted.filter((e) => !colorMap.has(getEntityKey(e)))
        : [];

    const seriesList: Series[] = [...topSeries];
    if (rest.length > 0) {
      const othersColor = resolveColor(CHART_COLORS[TOP_SERIES_COUNT]);
      colorMap.set(OTHERS_SERIES_ID, othersColor);
      seriesList.push({
        id: OTHERS_SERIES_ID,
        label: `${rest.length} others`,
        color: othersColor,
        total: rest.reduce((sum, e) => sum + (e.usage ?? 0), 0),
      });
    }

    const restIds = new Set(rest.map(getEntityKey));

    const allDays = new Set<string>();

    if (rangeStart && rangeEnd) {
      let cursor = rangeStart;
      while (!cursor.isAfter(rangeEnd)) {
        allDays.add(cursor.format('YYYY-MM-DD'));
        cursor = cursor.add(1, 'day');
      }
    }

    const isInRange = (day: string) => {
      const d = dayjs(day);
      if (rangeStart && d.isBefore(rangeStart, 'day')) return false;
      if (rangeEnd && d.isAfter(rangeEnd, 'day')) return false;
      return true;
    };

    sorted.forEach((e) =>
      e.dailyUsages?.forEach((d) => {
        if (d.day && isInRange(d.day)) allDays.add(d.day);
      })
    );
    detail?.dailyUsages?.forEach((d) => {
      if (d.day && isInRange(d.day)) allDays.add(d.day);
    });

    const dailyMap = new Map<string, Record<string, number>>();
    Array.from(allDays)
      .sort()
      .forEach((day) => dailyMap.set(day, {}));

    sorted.forEach((e) => {
      const entityId = getEntityKey(e);
      e.dailyUsages?.forEach((d) => {
        if (!d.day) return;
        const entry = dailyMap.get(d.day) ?? {};
        if (restIds.has(entityId)) {
          entry[OTHERS_SERIES_ID] =
            (entry[OTHERS_SERIES_ID] ?? 0) + (d.value ?? 0);
        } else if (colorMap.has(entityId)) {
          entry[entityId] = (entry[entityId] ?? 0) + (d.value ?? 0);
        }
        dailyMap.set(d.day, entry);
      });
    });

    // Populate __total__ for each day (total mode only).
    // Prefer detail.dailyUsages (already-summed API field) to avoid FP drift.
    if (mode === 'total') {
      const apiTotals = new Map<string, number>();
      detail?.dailyUsages?.forEach((d) => {
        if (d.day && d.value !== undefined && d.value !== null) {
          apiTotals.set(d.day, d.value);
        }
      });

      dailyMap.forEach((entry, day) => {
        const apiTotal = apiTotals.get(day);
        const computedTotal =
          apiTotal !== undefined
            ? apiTotal
            : Object.entries(entry)
                .filter(([k]) => k !== TOTAL_KEY)
                .reduce((sum, [, v]) => sum + v, 0);

        // Only set the key when the total is positive — a missing key returns
        // NaN from the dataKey accessor, which makes Recharts skip that day
        // and avoids flat zero segments on the dotted Total line.
        if (computedTotal > 0) {
          entry[TOTAL_KEY] = computedTotal;
        } else {
          delete entry[TOTAL_KEY];
        }
      });
    }

    const today = dayjs().format('YYYY-MM-DD');

    const points: ChartDataPoint[] = Array.from(allDays)
      .sort()
      .map((day) => {
        const values = dailyMap.get(day) ?? {};
        return {
          timestamp: day,
          chartTimestampUnix: dayjs(day).unix(),
          values: day > today && Object.keys(values).length === 0 ? {} : values,
        };
      });

    const hasTotalData =
      (detail?.totalUsage != null && detail.totalUsage > 0) ||
      (detail?.dailyUsages?.some((d) => (d.value ?? 0) > 0) ?? false);

    return {
      series: seriesList,
      chartData: points,
      entityColorMap: colorMap,
      hasTotalData,
    };
  }, [detail, periodFrom, periodTo, resolveColor, selectedEntityIds, mode]);

  const referenceBands = useMemo<ReferenceBand[]>(
    () =>
      (detail?.freeOfChargePeriods ?? [])
        .filter((p) => p.isEnabled && p.startDate && p.endDate)
        .map((p) => ({
          startDate: p.startDate!,
          endDate: p.endDate!,
          label: 'FREE OF CHARGE',
          tooltip:
            'Usage during this period is not billed and does not count against your credits.',
        })),
    [detail]
  );

  const referenceBandChecks = useMemo<ReferenceBandCheck[]>(
    () =>
      referenceBands.map((b) => ({
        startUnix: dayjs(b.startDate).startOf('day').unix(),
        endUnix: dayjs(b.endDate).endOf('day').unix(),
        label: b.label,
      })),
    [referenceBands]
  );

  const tooltipComponent = useMemo(
    () =>
      makeTooltipComponent({
        seriesInfo: series.map((s) => ({
          id: s.id,
          label: s.label,
          color: s.color,
        })),
        referenceBandChecks,
        unit: detail?.unit ?? '',
        totalColor: totalLineColor,
        mode,
      }),
    [series, referenceBandChecks, detail?.unit, totalLineColor, mode]
  );

  const visibleSeries =
    selectedEntityIds.length === 0
      ? series
      : series.filter((s) => selectedEntityIds.includes(s.id));

  useEffect(() => {
    // Don't push the synthetic Others id into the table color map.
    const tableColors = new Map(entityColorMap);
    tableColors.delete(OTHERS_SERIES_ID);
    onEntityColorMapChange?.(tableColors);
  }, [entityColorMap, onEntityColorMapChange]);

  const hasBands = referenceBands.length > 0;
  const unit = detail?.unit ?? '';
  const selection = useChartSelectionState();

  return (
    <LayoutCard
      title={title}
      headerEndContent={
        <Stack direction="row" alignItems="center" gap={8}>
          {headerControls}
          {actions}
        </Stack>
      }
      testId="usage-over-time-chart"
      slotProps={{
        title: { variant: 'P18B' },
        header: { py: 12 },
        // LayoutCard content defaults to overflow:hidden, which clips the
        // Recharts tooltip when it escapes the plot (allowEscapeViewBox).
        content: { sx: { pt: 0, overflow: 'visible' } },
      }}
    >
      <Stack gap={12}>
        {isLoading ? (
          <SummaryMetricsSkeleton />
        ) : (
          <SummaryMetricsRow>
            {detail?.totalUsage !== undefined ? (
              <SummaryMetric
                label={totalLabel}
                value={detail.totalUsage ?? 0}
                unit={unit}
              />
            ) : null}
            {mode === 'organizations' &&
              visibleSeries.map((s) => (
                <SummaryMetric
                  key={s.id}
                  label={s.label}
                  value={s.total}
                  unit={unit}
                  color={s.color}
                  isSelected={selection.isVisible(s.id)}
                  onClick={() => selection.toggleKey(s.id)}
                />
              ))}
          </SummaryMetricsRow>
        )}

        <ChartSelectionProvider
          selectedKeys={Array.from(selection.selectedKeys)}
          onSelectionChange={selection.setSelectedKeys}
        >
          <Box height={230} width="100%" sx={{ overflow: 'visible' }}>
            <ChartContainer<ChartDataPoint>
              data={chartData}
              isLoading={isLoading}
              analyzeWithOpsPilot={analyzeWithOpsPilot}
              sx={{
                height: '100%',
                overflow: 'visible',
                '& .recharts-tooltip-wrapper': { zIndex: 1300 },
              }}
              margin={
                hasBands
                  ? {
                      left: 0,
                      right: 0,
                      top: 12,
                      bottom: REFERENCE_BAND_MARGIN_BOTTOM,
                    }
                  : { left: 0, right: 0, top: 12, bottom: 0 }
              }
              testId="usage-over-time-chart-plot"
            >
              <ChartGrid hideVertical />

              {mode === 'organizations' &&
                series.map((s) => (
                  <Line<ChartDataPoint>
                    key={s.id}
                    selectionKey={s.id}
                    dataKey={(d: ChartDataPoint) => d.values[s.id] ?? NaN}
                    stroke={s.color}
                    strokeWidth={1.5}
                    dot={false}
                    tooltipConfig={{
                      displayName: s.label,
                      unit,
                    }}
                  />
                ))}

              {/*
               * Skip rendering the Total line entirely when there's no total
               * data (empty API dailyUsages + no entities).
               */}
              {mode === 'total' && hasTotalData && (
                <Line<ChartDataPoint>
                  dataKey={(d: ChartDataPoint) => d.values[TOTAL_KEY] ?? NaN}
                  stroke={totalLineColor}
                  strokeWidth={1.5}
                  strokeDasharray="4 4"
                  dot={false}
                  tooltipConfig={{
                    displayName: 'Total',
                    unit,
                  }}
                />
              )}

              {hasBands && (
                <ReferenceArea
                  x1={chartData[0]?.chartTimestampUnix ?? 0}
                  x2={chartData[chartData.length - 1]?.chartTimestampUnix ?? 0}
                  fill="transparent"
                  fillOpacity={0}
                  shape={() => (
                    <ReferenceBandStrip
                      bands={referenceBands.map((b) => ({
                        ...b,
                        color: b.color ?? tokens.theme.fg.status.success,
                      }))}
                      backgroundColor={tokens.theme.bg.status.successSubtle}
                      axisPaddingX={20}
                    />
                  )}
                />
              )}

              <YAxis<ChartDataPoint>
                tickCount={5}
                tickFormatter={yTickFormatter}
                padding={{ top: 16 }}
              />
              <TimeXAxis<ChartDataPoint>
                unixDataKey="chartTimestampUnix"
                ticksConfig={dailyTicksConfig}
              />

              {referenceBands.flatMap((band, i) => [
                <ReferenceArea
                  key={`band-start-${i}`}
                  x1={dayjs(band.startDate).startOf('day').unix()}
                  x2={dayjs(band.startDate).startOf('day').unix()}
                  stroke={band.color ?? tokens.theme.fg.status.success}
                  strokeDasharray="4 4"
                  strokeWidth={1}
                  fill="transparent"
                  fillOpacity={0}
                  ifOverflow="hidden"
                />,
                <ReferenceArea
                  key={`band-end-${i}`}
                  x1={dayjs(band.endDate).endOf('day').unix()}
                  x2={dayjs(band.endDate).endOf('day').unix()}
                  stroke={band.color ?? tokens.theme.fg.status.success}
                  strokeDasharray="4 4"
                  strokeWidth={1}
                  fill="transparent"
                  fillOpacity={0}
                  ifOverflow="hidden"
                />,
              ])}

              <ChartTooltip<ChartDataPoint>
                TooltipComponent={tooltipComponent}
                allowEscapeViewBox={{ x: true, y: true }}
              />
            </ChartContainer>
          </Box>
        </ChartSelectionProvider>
      </Stack>
    </LayoutCard>
  );
};
